import { routePaths } from 'src/utils/constants';

const CLAIM_PATH = routePaths.CLAIM_ACCOUNT;

// Query params that carry a credential-like value and never belong in analytics. `token` is the
// /claim link's (NPT-2091): it resolves to the traveler's email, and page_view fires on the first
// render, before the claim page has a chance to strip it from the URL.
const SENSITIVE_QUERY_PARAMS = ['token'];

/** `location.search` minus SENSITIVE_QUERY_PARAMS; undefined when nothing is left. */
export const sanitizeSearchForAnalytics = (search: string): string | undefined => {
   if (!search) return undefined;
   const params = new URLSearchParams(search);
   SENSITIVE_QUERY_PARAMS.forEach(name => params.delete(name));
   const rest = params.toString();
   return rest ? `?${rest}` : undefined;
};

/**
 * True while the URL still carries a param the page is about to strip itself (the /claim link's
 * `token`). page_view waits for the cleaned URL instead: Mixpanel stamps every event with the full
 * `$current_url`, so sanitizing our own `search` property alone would not keep it out.
 */
export const isAwaitingSensitiveParamStrip = (pathname: string, search: string): boolean =>
   pathname === CLAIM_PATH && SENSITIVE_QUERY_PARAMS.some(name => new URLSearchParams(search).has(name));

/**
 * A URL string with any /claim link `token` removed, for third-party payloads that carry full URLs
 * (Sentry events and breadcrumbs). Anything that is not a /claim URL, or does not parse, is
 * returned unchanged.
 */
export const scrubSensitiveUrl = (url: string | undefined | null): string | undefined | null => {
   if (!url || !url.includes('token=')) return url;
   try {
      const parsed = new URL(url, 'http://placeholder.invalid');
      if (parsed.pathname !== CLAIM_PATH) return url;
      SENSITIVE_QUERY_PARAMS.forEach(name => parsed.searchParams.delete(name));
      const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
      const rest = parsed.searchParams.toString();
      const path = `${parsed.pathname}${rest ? `?${rest}` : ''}${parsed.hash}`;
      return isAbsolute ? `${parsed.origin}${path}` : path;
   } catch {
      return url;
   }
};
